<template>
  <DSIconPickerCategories
    :model-value="selectedCategory"
    :disabled="props.disabled"
    @update:model-value="selectCategory"
    @hovered-category:change="setHoveredCategory"
  />

  <DSIconPickerSearch
    v-if="props.searchable"
    v-model="searchValue"
    :placeholder="searchPlaceholder"
    :disabled="props.disabled"
  />

  <DSIconPickerGrid
    v-if="filteredIcons.length"
    :key="gridResetKey"
    v-model="modelValue"
    :icons="visibleIcons"
    :has-more="hasMoreIcons"
    :disabled="props.disabled"
    @load-more="loadMoreIcons"
  />

  <DSIconPickerEmptyState v-else />
</template>

<script setup lang="ts">
import { computed, nextTick, ref, watch } from "vue";
import type { DSIconName } from "@/src/design_system/primitives/icon/types/iconName.generated";
import { ICON_PICKER_BATCH_SIZE } from "@/src/design_system/form_elements/icon_picker/constants/pagination";
import {
  ICON_PICKER_CATEGORIES,
  ICON_PICKER_CATEGORY_ID,
  type IconPickerCategoryId,
} from "@/src/design_system/form_elements/icon_picker/constants/categories";
import { filterIconPickerIcons } from "@/src/design_system/form_elements/icon_picker/utils/iconFilters";
import { getIconPickerCategoryLabel } from "@/src/design_system/form_elements/icon_picker/utils/categoryLabels";
import type { DSIconPickerListProps } from "@/src/design_system/form_elements/icon_picker/types/props";
import DSIconPickerCategories from "@/src/design_system/form_elements/icon_picker/components/DSIconPickerCategories.vue";
import DSIconPickerGrid from "@/src/design_system/form_elements/icon_picker/components/DSIconPickerGrid.vue";
import DSIconPickerEmptyState from "@/src/design_system/form_elements/icon_picker/components/DSIconPickerEmptyState.vue";
import DSIconPickerSearch from "@/src/design_system/form_elements/icon_picker/components/DSIconPickerSearch.vue";

const props = withDefaults(defineProps<DSIconPickerListProps>(), {
  searchable: true,
  disabled: false,
});

const modelValue = defineModel<DSIconName | null>({ default: null });

const searchValue = ref("");
const selectedCategory = ref<IconPickerCategoryId>(ICON_PICKER_CATEGORY_ID.All);
const loadedIconCount = ref(ICON_PICKER_BATCH_SIZE);
const isLoadMorePending = ref(false);
const hoveredCategoryId = ref<IconPickerCategoryId | null>(null);
const gridResetKey = computed(() => `${selectedCategory.value}-${searchValue.value}`);

const searchPlaceholder = computed(() => {
  if (props.disabled) return undefined;

  const hoveredCategory = ICON_PICKER_CATEGORIES.find(category => category.id === hoveredCategoryId.value);

  return hoveredCategory ? getIconPickerCategoryLabel(hoveredCategory) : undefined;
});

const filteredIcons = computed(() => {
  return filterIconPickerIcons({
    selectedCategory: selectedCategory.value,
    searchQuery: props.searchable ? searchValue.value : "",
  });
});

const selectedIconCount = computed(() => {
  const selectedIcon = modelValue.value;
  if (!selectedIcon) return ICON_PICKER_BATCH_SIZE;

  const selectedIconIndex = filteredIcons.value.indexOf(selectedIcon);
  if (selectedIconIndex === -1) return ICON_PICKER_BATCH_SIZE;

  return selectedIconIndex + 1;
});

const visibleIconCount = computed(() => Math.max(loadedIconCount.value, selectedIconCount.value));

const visibleIcons = computed(() => {
  return filteredIcons.value.slice(0, visibleIconCount.value);
});

const hasMoreIcons = computed(() => visibleIconCount.value < filteredIcons.value.length);

const loadMoreIcons = (): void => {
  if (!hasMoreIcons.value || isLoadMorePending.value) return;

  isLoadMorePending.value = true;
  loadedIconCount.value = Math.min(visibleIconCount.value + ICON_PICKER_BATCH_SIZE, filteredIcons.value.length);

  nextTick(() => {
    isLoadMorePending.value = false;
  });
};

const resetVisibleIcons = (): void => {
  isLoadMorePending.value = false;
  loadedIconCount.value = ICON_PICKER_BATCH_SIZE;
};

const selectCategory = (category: IconPickerCategoryId): void => {
  if (props.searchable && searchValue.value.trim()) {
    searchValue.value = "";
  }

  selectedCategory.value = category;

  resetVisibleIcons();
};

const setHoveredCategory = (categoryId: IconPickerCategoryId | null): void => {
  hoveredCategoryId.value = categoryId;
};

watch([searchValue, () => props.searchable], ([currentSearchValue, searchable]) => {
  if (searchable && currentSearchValue.trim() && selectedCategory.value !== ICON_PICKER_CATEGORY_ID.All) {
    selectedCategory.value = ICON_PICKER_CATEGORY_ID.All;
  }

  resetVisibleIcons();
});
</script>
